import React, { useEffect } from 'react';
import { Grid, Typography, Theme } from '@material-ui/core';
import qs from 'qs';
import { push } from 'connected-react-router';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import clsx from 'clsx';

import yup from '../../../utility/yup';
import { useActionDispatcher, useTitle } from '../../../hooks';
import { passwordResetRequest, resetChanged } from '../../../store/modules/password-recovery/actions';
import { PasswordResetPayload } from '../../../store/modules/password-recovery/types';
import { passwordSchema } from '../../../components/password-error/PasswordError';
import {
  Error,
  FormPasswordInput,
  FormSubmit,
  Link,
  PagesTitle,
} from '../../../components';
import {
  selectPasswordRecovery,
} from '../../../store/modules/password-recovery/selectors';
import PageContainer from '../PageContainer';

const useStyles = makeStyles(({ colors, palette }: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    flexGrow: 1,
  },
  subtitle: {
    color: palette.text.primary,
    fontSize: '16px',
    marginTop: '16px',
  },
  control: {
    marginTop: '33px',
  },
  controlLabel: {
    color: palette.text.primary,
    fontSize: '14px',
    fontFamily: 'Roboto',
  },
  submit: {
    height: '40px',
    borderRadius: '5px',
    fontWeight: 600,
    fontFamily: 'Montserrat',
    color: palette.common.white,
    textTransform: 'none',
    width: '100%',
  },
  forgot: {
    display: 'block',
    fontSize: '14px',
    marginTop: '10px',
  },
}), { name: 'PasswordRecovery' });

const emailSchema = yup.object().shape({
  new_password: passwordSchema,
});

const PasswordReset = () => {
  const classes = useStyles();

  useTitle('Forgot your password?');

  const dispatch = useDispatch();

  const handleReset = useActionDispatcher(passwordResetRequest);

  const { loading, error, changed } = useSelector(selectPasswordRecovery);

  const errorMsg = (error) ? 'There was an error resetting your password.' : null;

  useEffect(() => {
    if (changed) {
      dispatch(resetChanged());
      dispatch(push('/pages/password-recover-success'));
    }
  }, [dispatch, changed]);

  const query = window.location.search;
  const { token, invitation_id } = qs.parse(query, { ignoreQueryPrefix: true });

  const initialValues = {
    new_password: '',
    token,
    invitation_id,
  };

  return (
    <PageContainer>
      <Grid container className={classes.root}>
        <PagesTitle>Forgot your Password</PagesTitle>
        <Error className={classes.subtitle}>{errorMsg}</Error>
        <Typography variant="subtitle1" className={classes.subtitle}>
          Please try again to enter your password.
        </Typography>
        <Grid item xs={12} container>
          <Formik
            <PasswordResetPayload>
            initialValues={initialValues}
            onSubmit={handleReset}
            validationSchema={emailSchema}
          >
            <Form className={classes.form}>
              <FormPasswordInput
                className={classes.control}
                fullWidth
                name="new_password"
                label="New Password"
                autoComplete="new-password"
              />
              <FormSubmit
                className={clsx(classes.submit, classes.control)}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Reset Password
              </FormSubmit>
            </Form>
          </Formik>
        </Grid>
        <Typography variant="subtitle1" className={classes.subtitle}>
          <Link to="/">
            Need Help?
          </Link>
        </Typography>
      </Grid>
    </PageContainer>
  );
};

export default PasswordReset;
